import React, { useMemo } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
} from "reactstrap";
import ModalHeader from "@dhn/components/molecules/modal-header";
import ReactTable from "@dhn/components/molecules/data-table";
import CardHeaderTabs from "@dhn/components/molecules/card-header-tabs";
import useActions from "./useActions";
import LoadingDhn from "@dhn/components/Loading/LoadingDhn";
import moment from "moment";
import DatePicker from "react-datepicker";
import ReactFormat from "react-number-format";

const TRADITIONAL_SEQUENCE_LENGTH = 8;
const ELECTRONIC_SEQUENCE_LENGTH = 10;
const CONTROL_KEYS = new Set([
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Tab",
  "Home",
  "End",
]);

const getSequenceLength = (prefix) =>
  String(prefix || "").startsWith("E")
    ? ELECTRONIC_SEQUENCE_LENGTH
    : TRADITIONAL_SEQUENCE_LENGTH;

const normalizeEndSequence = (value, sequenceLength) =>
  String(value || "")
    .replace(/\D/g, "")
    .slice(-sequenceLength)
    .padStart(sequenceLength, "0");

const clearSequenceRange = (value, start, end, sequenceLength) => {
  const sequence = normalizeEndSequence(value, sequenceLength).split("");
  const safeStart = Math.max(0, Math.min(start, sequenceLength));
  const safeEnd = Math.max(safeStart, Math.min(end, sequenceLength));

  for (let index = safeStart; index < safeEnd; index += 1) {
    sequence[index] = "0";
  }

  return sequence.join("");
};

const insertSequenceDigits = (
  value,
  position,
  replacement,
  sequenceLength,
) => {
  const sequence = normalizeEndSequence(value, sequenceLength).split("");
  const safePosition = Math.max(0, Math.min(position, sequenceLength));
  const digits = String(replacement || "").replace(/\D/g, "").split("");
  let cursor = safePosition;

  if (safePosition === sequenceLength) {
    digits.forEach((digit) => {
      sequence.shift();
      sequence.push(digit);
    });

    return { value: sequence.join(""), cursor: sequenceLength };
  }

  digits.some((digit) => {
    if (cursor >= sequenceLength) return true;

    sequence[cursor] = digit;
    cursor += 1;
    return false;
  });

  return { value: sequence.join(""), cursor };
};

const setInputCursor = (input, position) => {
  window.requestAnimationFrame(() => {
    input.setSelectionRange(position, position);
  });
};

export default function ModalEditTaxReceipt({
  isOpen,
  toggle,
  receiptData,
  dataDoctor,
}) {
  const sequenceLength = getSequenceLength(receiptData?.prefix);
  const [{ state }, actions] = useActions({
    taxVoucherId: receiptData?.id,
    dataDoctor,
    isOpen,
    sequenceLength,
  });

  const {
    activeTab,
    data,
    isLoading,
    tabIssued: { data: tabIssuedData },
    tabDefeated: { data: tabDefeatedData },
  } = state;

  const {
    onChange,
    restoreState,
    loadingTypeTaxVouchers,
    onChangeData,
    onActionSaveOrUpdate,
  } = actions;

  const endSequenceValue = normalizeEndSequence(data?.endSeq, sequenceLength);

  const updateEndSequence = (value) => {
    onChangeData({
      target: {
        name: "endSeq",
        value: normalizeEndSequence(value, sequenceLength),
      },
    });
  };

  const handleEndSequenceKeyDown = (event) => {
    const { key, currentTarget, ctrlKey, metaKey } = event;

    if (ctrlKey || metaKey || CONTROL_KEYS.has(key)) return;

    if (/^\d$/.test(key)) {
      event.preventDefault();
      const { selectionStart = 0, selectionEnd = 0 } = currentTarget;
      const baseSequence = selectionStart !== selectionEnd
        ? clearSequenceRange(endSequenceValue, selectionStart, selectionEnd, sequenceLength)
        : endSequenceValue;
      const nextSequence = insertSequenceDigits(
        baseSequence,
        selectionStart === selectionEnd ? selectionEnd : selectionStart,
        key,
        sequenceLength,
      );

      updateEndSequence(nextSequence.value);
      setInputCursor(currentTarget, nextSequence.cursor);
      return;
    }

    if (key === "Backspace" || key === "Delete") {
      event.preventDefault();
      const { selectionStart = 0, selectionEnd = 0 } = currentTarget;
      const hasSelection = selectionStart !== selectionEnd;

      if (
        key === "Backspace" &&
        !hasSelection &&
        selectionStart === sequenceLength
      ) {
        updateEndSequence(`0${endSequenceValue.slice(0, -1)}`);
        setInputCursor(currentTarget, sequenceLength);
        return;
      }

      const start = key === "Backspace" && !hasSelection
        ? Math.min(selectionStart, sequenceLength - 1)
        : selectionStart;
      const end = key === "Delete" && !hasSelection
        ? Math.min(selectionEnd + 1, sequenceLength)
        : hasSelection
          ? selectionEnd
          : Math.min(selectionStart + 1, sequenceLength);
      const nextValue = clearSequenceRange(
        endSequenceValue,
        start,
        end,
        sequenceLength,
      );

      updateEndSequence(nextValue);
      setInputCursor(
        currentTarget,
        key === "Backspace" && !hasSelection ? end : start,
      );
      return;
    }

    event.preventDefault();
  };

  const handleEndSequencePaste = (event) => {
    event.preventDefault();
    const digits = event.clipboardData.getData("text").replace(/\D/g, "");
    if (!digits) return;

    const { currentTarget } = event;
    const { selectionStart = 0, selectionEnd = 0 } = currentTarget;
    const baseSequence = selectionStart !== selectionEnd
      ? clearSequenceRange(endSequenceValue, selectionStart, selectionEnd, sequenceLength)
      : endSequenceValue;
    const nextSequence = insertSequenceDigits(
      baseSequence,
      selectionStart === selectionEnd ? selectionEnd : selectionStart,
      digits,
      sequenceLength,
    );

    updateEndSequence(nextSequence.value);
    setInputCursor(currentTarget, nextSequence.cursor);
  };

  const emitidosColumns = useMemo(
    () => [
      {
        Header: "No. Comprobante",
        dataType: "fiscalNumber",
        className: "text-center",
        Cell: ({ original }) => {
          return original?.name || `${receiptData?.prefix || ""}${original.lastSeq || ""}`;
        },
      },
      {
        Header: "Fecha",
        className: "text-center",
        Cell: ({ original }) =>
          original?.date ? moment(original.date).format("DD/MM/YYYY") : "N/D",
      },
      {
        Header: "Receptor",
        accessor: "receiver",
      },
      {
        Header: "RNC",
        accessor: "rnc",
        className: "text-center",
      },
      {
        Header: "Monto",
        className: "text-right",
        Cell: ({ original }) => (
          <ReactFormat
            style={{ textAlign: "right", display: "block" }}
            value={original?.amount || "00.00"}
            thousandSeparator={true}
            fixedDecimalScale={true}
            decimalScale={2}
            displayType="text"
            prefix={"$"}
          />
        ),
      },
      {
        Header: "Vencimiento",
        className: "text-center",
        Cell: ({ original }) =>
          original?.expirationDate
            ? moment(original.expirationDate).format("DD/MM/YYYY")
            : "N/D",
      },
    ],
    [receiptData?.prefix],
  );

  const vencidosColumns = useMemo(
    () => [
      {
        Header: "No. Comprobante",
        dataType: "fiscalNumber",
        className: "text-center",
        Cell: ({ original }) => {
          return original?.name || `${receiptData?.prefix || ""}${original.lastSeq || ""}`;
        },
      },
      {
        Header: "Vencimiento",
        className: "text-center",
        Cell: ({ original }) =>
          original?.expirationDate
            ? moment(original.expirationDate).format("DD/MM/YYYY")
            : "N/D",
      },
    ],
    [receiptData?.prefix],
  );

  if (loadingTypeTaxVouchers) return <LoadingDhn isOpen={true} />;

  return (
    <div className="animated fadeIn">
      <Modal isOpen={isOpen} toggle={toggle} size="xl" centered>
        <ModalHeader
          toggle={() => {
            restoreState();
            toggle();
          }}
          iconClass="fa-file-text-o"
          description="Modificar comprobante fiscal"
          subtitle="Complete la secuencia, el umbral de alerta y el vencimiento del comprobante."
        />
        <ModalBody>
          <Row form className="mb-3">
            <Col md={3}>
              <Label>Tipo de Comprobante:</Label>
              <Input disabled value={receiptData?.name || ""} />
            </Col>
            <Col md={1}>
              <Label>Prefijo:</Label>
              <Input disabled value={receiptData?.prefix || ""} />
            </Col>
            <Col md={2}>
              <Label>Final de secuencia: </Label>
              <Input
                type="text"
                name="endSeq"
                value={endSequenceValue}
                inputMode="numeric"
                maxLength={sequenceLength}
                onKeyDown={handleEndSequenceKeyDown}
                onPaste={handleEndSequencePaste}
                onChange={({ target: { value } }) => updateEndSequence(value)}
                onFocus={({ target }) =>
                  target.setSelectionRange(sequenceLength, sequenceLength)
                }
              />
            </Col>
            <Col md={2}>
              <Label>Umbral de alerta: </Label>
              <Input
                type="number"
                name="sequenceAlert"
                value={data?.sequenceAlert || ""}
                onChange={onChangeData}
                placeholder="Ej.: 100"
              />
            </Col>
            <Col md={2}>
              <Label>Nuevo Fin de Secuencia:</Label>
              <DatePicker
                selected={
                  data?.expirationDate
                    ? moment(data.expirationDate, "YYYY-MM-DD", true).isValid()
                      ? moment(data.expirationDate, "YYYY-MM-DD", true).toDate()
                      : moment(data.expirationDate).toDate()
                    : null
                }
                onChange={(date) =>
                  onChangeData({
                    target: {
                      name: "expirationDate",
                      value: date ? moment(date).format("YYYY-MM-DD") : "",
                    },
                  })
                }
                minDate={moment().add(1, "day").toDate()}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                className="form-control"
              />
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button
                color="primary"
                className="btn-color-primary"
                disabled={isLoading}
                onClick={onActionSaveOrUpdate}
              >
                {isLoading ? (
                  <>
                    <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
                    {" Guardando..."}
                  </>
                ) : (
                  <>
                    <i className="fa fa-plus" />
                    {" Agregar"}
                  </>
                )}
              </Button>
            </Col>
          </Row>
          <Card>
            <Row form>
              <Col md={12}>
                <CardHeaderTabs
                  active={activeTab}
                  onClick={(tab) => onChange("activeTab", tab)}
                  tabs={[
                    {
                      id: 1,
                      name: "Emitidos",
                    },
                    {
                      id: 2,
                      name: "Vencidos",
                    },
                  ]}
                />
              </Col>
            </Row>
            <CardBody>
              <Row form>
                <Col md={12}>
                  <ReactTable
                    className="-striped -highlight"
                    defaultPageSize={10}
                    noDataText="No hay datos para mostrar"
                    data={
                      activeTab === 1
                        ? tabIssuedData || []
                        : tabDefeatedData || []
                    }
                    columns={
                      activeTab === 1 ? emitidosColumns : vencidosColumns
                    }
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>
        </ModalBody>
        <ModalFooter>
          <Button
            color="success"
            className="btn-color-success"
            onClick={() => {
              restoreState();
              toggle();
            }}
          >
            <i className="fa fa-check"></i>
            {" Listo"}
          </Button>
          <Button
            color="danger"
            className="btn-color-danger"
            onClick={() => {
              restoreState();
              toggle();
            }}
          >
            <i className="fa fa-times"></i>
            {" Cancelar"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
